import { FormControl, ListSubheader, MenuItem, Select } from '@mui/material'
import { useCallback } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from '../../../../app/hooks'
import logger from '../../../../app/middleware/log'
import { switchUserContext } from '../../../api'
import { fetchLoggedInUserThunk } from '../../../slice'
import { ILoggedInUser } from '../../../types'
import messages from './messages'

export interface ICustomerTenantSelectProps {
  user: ILoggedInUser
}

export const getInitialValue = (user: ILoggedInUser | null): string | null => {
  if (!user) return null

  if (user.customerId) {
    const selectedCustomer = user.customers.find((customer) => customer.id === user.customerId)
    return selectedCustomer ? `customer-${selectedCustomer.id}` : null
  }

  if (user.tenantId) {
    const selectedTenant = user.tenants.find((tenant) => tenant.id === user.tenantId)
    return selectedTenant ? `tenant-${selectedTenant.id}` : null
  }

  return null
}

const CustomerTenantSelect = ({ user }: ICustomerTenantSelectProps) => {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const selectedValue = useCallback(() => getInitialValue(user), [user])

  const handleChange = async (event: any) => {
    const value = event.target.value

    // Determine the tenantId and customerId based on the selection
    let tenantId: number
    let customerId: number | undefined

    if (value.includes('customer-')) {
      customerId = parseInt(value.split('-')[1], 10)
      // Use the current user's tenantId since customer users belong to a tenant
      tenantId = user.tenantId
    } else {
      tenantId = parseInt(value.split('-')[1], 10)
      customerId = undefined
    }

    // Try to call the switch-context API to update the B2C extension attribute
    // This is optional and will fail until the backend is deployed
    try {
      await switchUserContext(tenantId, customerId)
    } catch (error) {
      // Log the error but continue - the user fetch will still work via query params
      logger.error('Error calling switch-context API (continuing with user fetch):', error)
    }

    // Fetch the logged-in user with the new context
    if (value.includes('customer-')) {
      dispatch(fetchLoggedInUserThunk({ customerId: customerId }))
    } else {
      dispatch(fetchLoggedInUserThunk({ tenantId: tenantId }))
    }

    localStorage.setItem('selectedCustomerTenant', value)
  }

  return (
    <FormControl variant='standard' style={{ minWidth: 200 }}>
      <Select
        labelId='customer-tenant-select-label'
        value={selectedValue()}
        onChange={handleChange}
        style={{ borderBottom: 'none' }}
      >
        {user.tenants.length > 0 && (
          <ListSubheader>{formatMessage(messages.tenants)}</ListSubheader>
        )}
        {user.tenants.map((tenant) => (
          <MenuItem key={`tenant-${tenant.id}`} value={`tenant-${tenant.id}`} data-type='tenantId'>
            {tenant.name}
          </MenuItem>
        ))}
        {user.customers.length > 0 && (
          <ListSubheader>{formatMessage(messages.customers)}</ListSubheader>
        )}
        {user.customers.map((customer) => (
          <MenuItem
            key={`customer-${customer.id}`}
            value={`customer-${customer.id}`}
            data-type='customerId'
          >
            {customer.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  )
}

export default CustomerTenantSelect
